import { motion } from "framer-motion";
import { Gift, Zap, TrendingUp, Crown, Check, ArrowRight, Sparkles, Rocket } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import {
  PLAN_LIST,
  AI_FEATURE_ROWS,
  FREE_FEATURE_ROWS,
  formatLimit,
  formatDevices,
} from "../../lib/subscription-plans";
import { FAQS, CHROME_URL } from "../../lib/landing-data";
import { useAuthDialog } from "../../lib/auth-dialog-context";

const PLAN_ICONS = { Gift, Zap, TrendingUp, Crown };

const SectionLabel = ({ children }) => (
  <div className="mt-4 mb-1.5 text-[9px] font-bold uppercase tracking-widest text-slate-400">
    {children}
  </div>
);

const Divider = () => <div className="my-1 border-t border-slate-100" />;

const FeatureRow = ({ label, value, colorClass }) => (
  <div className="flex items-center justify-between py-[5px]">
    <div className="flex items-center gap-2 min-w-0">
      <Check className="w-3 h-3 text-slate-300 shrink-0" strokeWidth={2} />
      <span className="text-[13px] text-slate-600 truncate">{label}</span>
    </div>
    <span className={`text-[13px] font-semibold shrink-0 ml-2 ${colorClass}`}>{value}</span>
  </div>
);

export const Pricing = () => {
  const { openAuth } = useAuthDialog();

  return (
    <section id="pricing" className="relative py-20 sm:py-28 bg-gradient-to-b from-purple-50/40 via-white to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider">
            Pricing
          </span>
          <h2 className="font-heading mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
            Simple, predictable pricing
          </h2>
          <p className="mt-4 text-slate-600 text-base sm:text-lg">
            Start free. Upgrade as you grow. No credits to count.
          </p>
        </motion.div>

        {/* Plan cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {PLAN_LIST.map((plan, i) => {
            const Icon = PLAN_ICONS[plan.icon] || Zap;
            const isPopular = plan.popular;
            const isPro = plan.id === "pro";

            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.07 }}
                whileHover={{ y: -4 }}
                data-testid={`pricing-plan-${plan.id}`}
                className={`relative rounded-3xl p-[2px] ${
                  isPopular
                    ? "bg-gradient-to-br from-purple-600 via-violet-600 to-fuchsia-600 shadow-2xl shadow-purple-600/30"
                    : isPro
                    ? "bg-gradient-to-br from-violet-500 to-purple-700 shadow-lg shadow-violet-500/20"
                    : "bg-slate-200"
                }`}
              >
                <div className="relative h-full rounded-[22px] bg-white p-5 flex flex-col overflow-hidden">
                  {isPopular && (
                    <div className="absolute -top-20 -right-20 w-44 h-44 rounded-full bg-purple-100/60 blur-3xl" />
                  )}

                  {/* Badge */}
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold w-fit ${
                      isPopular
                        ? "bg-purple-100 text-purple-700"
                        : isPro
                        ? "bg-violet-100 text-violet-700"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {plan.badge}
                  </span>

                  {/* Name & price */}
                  <div className="relative mt-3">
                    <h3 className="font-heading text-2xl font-extrabold text-slate-900">{plan.name}</h3>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span
                        className={`font-heading text-4xl font-extrabold tabular-nums ${
                          isPopular
                            ? "bg-gradient-to-br from-purple-700 to-violet-600 bg-clip-text text-transparent"
                            : "text-slate-900"
                        }`}
                      >
                        {plan.priceLabel}
                      </span>
                      <span className="text-sm text-slate-500">/ {plan.period}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">{plan.tagline}</div>
                  </div>

                  <Divider />

                  {/* AI Features — Daily */}
                  <SectionLabel>AI Features — Daily</SectionLabel>
                  {AI_FEATURE_ROWS.map(({ key, label }) => {
                    const { label: valLabel, colorClass } = formatLimit(plan.limits[key]);
                    return <FeatureRow key={key} label={label} value={valLabel} colorClass={colorClass} />;
                  })}

                  <Divider />

                  {/* Free Features */}
                  <SectionLabel>Free Features</SectionLabel>
                  {FREE_FEATURE_ROWS.map(({ key, label }) => (
                    <FeatureRow key={key} label={label} value="Unlimited" colorClass="text-emerald-600" />
                  ))}

                  <Divider />

                  {/* Device Limit */}
                  <SectionLabel>Device Limit</SectionLabel>
                  <FeatureRow
                    label="Max devices"
                    value={formatDevices(plan.devices).label}
                    colorClass={formatDevices(plan.devices).colorClass}
                  />
                  <FeatureRow
                    label="Login elsewhere"
                    value={plan.loginElsewhereLabel}
                    colorClass={
                      plan.loginElsewhere === "no_restriction" ? "text-emerald-600" : "text-slate-500"
                    }
                  />

                  {/* CTA */}
                  <Button
                    onClick={() => openAuth("signup")}
                    data-testid={`pricing-plan-${plan.id}-cta`}
                    className={`relative mt-5 w-full rounded-full h-11 font-semibold text-sm ${
                      isPopular
                        ? "bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white shadow-lg shadow-purple-600/30"
                        : isPro
                        ? "bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800 text-white shadow-md shadow-violet-500/20"
                        : "bg-slate-900 hover:bg-slate-800 text-white"
                    }`}
                  >
                    {plan.cta} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export const FAQ = () => {
  return (
    <section id="faq" className="relative py-20 sm:py-28 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider">
            FAQ
          </span>
          <h2 className="font-heading mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
            Frequently asked questions
          </h2>
        </motion.div>

        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`item-${i}`}
              data-testid={`faq-item-${i}`}
              className="rounded-2xl border border-slate-200 bg-white px-5 hover:border-purple-200 transition-colors data-[state=open]:border-purple-300 data-[state=open]:bg-purple-50/30"
            >
              <AccordionTrigger className="font-heading text-left text-base sm:text-lg font-bold text-slate-900 hover:no-underline py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-sm sm:text-base leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export const FinalCTA = () => {
  const { openAuth } = useAuthDialog();
  return (
    <section className="relative py-20 sm:py-28">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          data-testid="final-cta-banner"
          className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-purple-700 via-violet-700 to-fuchsia-700 p-10 sm:p-16 text-center shadow-2xl shadow-purple-900/30"
        >
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-fuchsia-400/30 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-purple-400/30 blur-3xl" />
          <div className="relative">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              Start your free 3-day trial
            </h2>
            <p className="mt-5 text-base sm:text-lg text-purple-100 max-w-2xl mx-auto leading-relaxed">
              No card needed. Try AI listing, title optimise, and more instantly. Upgrade whenever you're ready.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => openAuth("signup")}
                size="lg"
                data-testid="final-cta-button"
                className="rounded-full bg-white hover:bg-purple-50 text-purple-700 font-bold px-8 h-12 shadow-xl"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Start Free Trial
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                data-testid="final-cta-chrome"
                className="rounded-full border-white/30 bg-white/10 text-white hover:bg-white/20 font-semibold px-7 h-12"
              >
                <a href={CHROME_URL}>
                  <Rocket className="w-4 h-4 mr-2" /> Add to Chrome
                </a>
              </Button>
            </div>
            <div className="mt-4 text-xs text-purple-200">
              Free 3-day trial · No card required · Upgrade anytime
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
